import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { SecurityService } from '../../../core/services/security.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BatchProcessPersonSearchComponent } from '../../document/batch-process/batch-process-person-search/batch-process-person-search.component';
import { ConfirmationModalComponent } from '@nx-ahs/shared/components';
import { SearchService } from '@nx-ahs/search';

@Component({
	selector: 'nx-ahs-formly-person-search',
	templateUrl: './person-search.component.html',
	styleUrl: './person-search.component.css',
	standalone: false
})
export class PersonSearchComponent extends FieldType<FieldTypeConfig> implements OnInit {
	public allowCreatePerson: boolean = true;
	public avform: FormGroup;
	public readonly: boolean = false;
	public rpaClientSettings: any = {};
	public searchOpen: boolean = false;
	public personDisplayProps = {
		label: 'Person',
		isVisible: true
	};
	public searchId: number = 3;
	public personInsertScriptId: number;
	public isFormlyScript: boolean = false;
	public personInsertTitle: string = 'Add Member';

	constructor(
		private dialogService: MatDialog,
		private securityService: SecurityService,
		private formBuilder: FormBuilder,
		private searchService: SearchService
	) {
		super();
	}

	ngOnInit(): void {
		this.readonly = this.formState?.disabled || this.props.disabled;
		this.searchId = this.props.searchId || 3;
		this.personInsertScriptId =
			this.props.personInsertId ||
			this.securityService.getCurrentApp().application.clientConfig.insertPerson.insertPersonScriptDefinitionId;
		this.allowCreatePerson = this.props.allowCreatePerson || true;
		this.isFormlyScript = this.props.isFormlyScript || false;
		this.personInsertTitle = this.props.personInsertTitle || 'Add Member';

		this.avform = this.formBuilder.group({
			person: new FormControl(''),
			personDisplay: new FormControl('')
		});

		(this.form as FormGroup).addControl(this.key.toString(), this.formControl);
		this.form.updateValueAndValidity();

		if (this.props?.selected?.name) {
			this.personDisplayProps.label = this.props?.selected?.name;
		}

		if (this.props?.selected?.nameVisible) {
			this.personDisplayProps.isVisible = false;
		}

		if (this.props?.required) {
			this.avform.get('personDisplay').setValidators([Validators.required]);
			this.avform.get('personDisplay').updateValueAndValidity();
			this.avform.get('personDisplay').markAsTouched();
			this.avform.get('personDisplay').updateValueAndValidity();
		}

		if (this.props?.disabled) {
			this.avform.get('personDisplay').disable();
		}

		if (this.formControl.value) {
			const personId = this.formControl.value;
			this.search(personId).then((results) => {
				if (results[0]) {
					this.avform.get('person').setValue(results[0]);
					this.avform.get('person').updateValueAndValidity();
					this.avform.get('personDisplay').setValue(results[0].name);
					this.avform.get('personDisplay').updateValueAndValidity();
					this.formControl.setValue(results[0]?.personId || null);
					this.formControl.updateValueAndValidity();
				}
			});
		}
	}

	public openPersonSearch(): void {
		if (!this.readonly && !this.searchOpen) {
			this.searchOpen = true;

			this.avform.markAllAsTouched();

			this.dialogService
				.open(BatchProcessPersonSearchComponent, {
					disableClose: true,
					minWidth: '800px',
					maxHeight: '1000px',
					hasBackdrop: true,
					backdropClass: 'darker-overlay-backdrop',
					panelClass: 'default-modal',
					data: {
						criteriaValues: {
							tokens: this.props?.criteriaValues?.tokens || null,
							firstName: this.props?.criteriaValues?.firstName || null,
							lastName: this.props?.criteriaValues?.lastName || null,
							genderId: this.props?.criteriaValues?.genderId || null,
							sSN: this.props?.criteriaValues?.sSN || null,
							birthDate: this.props?.criteriaValues?.birthDate || null,
							accountNumber: this.props?.criteriaValues?.accountNumber || null,
							idNumber: this.props?.criteriaValues?.idNumber || null,
							address: this.props?.criteriaValues?.address || null,
							phoneNumber: this.props?.criteriaValues?.phoneNumber || null,
							orderBy: this.props?.criteriaValues?.orderBy || null,
							city: this.props?.criteriaValues?.city || null,
							zip: this.props?.criteriaValues?.zip || null,
							eligibilityCountyId: this.props?.criteriaValues?.eligibilityCountyId || null,
							dateRangeId: this.props?.criteriaValues?.dateRangeId,
							personTypeId: this.props?.criteriaValues?.personTypeId || null
						},
						isEncounter: false,
						allowCreatePerson: this.allowCreatePerson,
						searchId: this.searchId,
						personInsertScriptId: this.personInsertScriptId,
						isFormlyScript: this.isFormlyScript,
						personInsertTitle: this.personInsertTitle,
						canCustomizeView: false
					}
				})
				.afterClosed()
				.toPromise()
				.then((result) => {
					//console.log(result);
					//successfully rpa successfully found the person
					if (result?.person) {
						this.avform.get('person').setValue(result.person);
						this.formControl.setValue(result.person?.personId || null);
					}
					//person not found outside of healthTrack
					else if (result && result != false) {
						this.avform.get('person').setValue(result);

						if (this.rpaClientSettings?.useRPA) {
							this.dialogService.open(ConfirmationModalComponent, {
								width: '75%',
								minWidth: '400px',
								maxWidth: '800px',
								maxHeight: '800px',
								position: {
									left: '75px'
								},
								data: {
									showCancel: false,
									title: 'RPA Result',
									message: this.rpaClientSettings?.messageResultNotFound
								}
							});
						}
					}

					this.searchOpen = false;
					if (result?.name != null) {
						this.avform.get('personDisplay').setValue(result.name);
						//this.changeName(false, docIndex);
					}

					this.formControl.setValue(result?.personId || null);

					if (this.props?.patchSiblings && result && result !== false) {
						this.field.parent?.formControl?.patchValue({
							personId: result.personId ?? null,
							firstName: result.firstName ?? null,
							lastName: result.lastName ?? null,
							name: result.name ?? null,
							displayName: result.name ?? null,
							accountId: result.accountId ?? null,
							accountTypeId: result.accountTypeId ?? null,
							accountNumber: result.accountNumber ?? null
						});
					}
				});
		}
	}

	public removePerson() {
		if (!this.readonly) {
			this.avform.get('person').reset();
			this.avform.get('personDisplay').reset();
			this.avform.markAllAsTouched();

			this.formControl.setValue(null);
		}
	}

	private search(personId: any): Promise<any[]> {
		return this.searchService.search(1, { personId: personId }, false).toPromise();
	}
}
